import { useEffect, useMemo, useState, useContext, useRef } from 'react';

import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { MaterialReactTable } from 'material-react-table';
import { ThemeProvider } from '@mui/material';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { Delete, Edit, MonetizationOn, MoneyOff, PictureAsPdf, Visibility, Download } from '@mui/icons-material';
import PrintIcon from '@mui/icons-material/Print';

import { format, isValid } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Context } from '../../../context/UserContext';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';

import { useMRTState } from '../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { MenuItem, ListItemIcon, ListItemText, useMediaQuery, useTheme } from '@mui/material';

import ResponsiveToolbarActions from '../../layout/ResponsiveToolbarActions';

import CustomDialog from '../../layout/CustomDialog';
import tableThemeCustom from '../../form/tableThemeCustom';

import tratar, { getApiUrl } from '../../../utils/retornaTratamentoURL';
import { obterInicioEFimDoMes } from '../../../utils/financeiro';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import {
    obterPermissoesUsuario,
    resolveEmpresaTabela,
    precisaCorrigirDataColumnFiltersObjeto,
    corrigirDataColumnFiltersObjeto,
} from '../../../utils/funcoes';

import * as EmpresaAPI from '../../../api/empresa';
import * as LancamentosAPI from '../../../api/lancamentos';
import * as SituacaoAPI from '../../../api/situacao';
import { relatorios } from '../../../api/relatorios';

import { updateSelectedComponents, clearSelectedComponent } from '../../../store/reducers/actions';

import {
    podeImprimir,
    podeIncluir,
    podeEditar,
    podeBaixar,
    podeCancelarBaixa,
    podeImprimirAnalitico,
    podeImprimirGerencia,
    podeCancelar,
} from '../../../hooks/usePermisionMRT';

import { getFinanceRouteParams, buildFinanceParams } from './helpers/financeRouteParams';
import { getLancamentosColumns } from './helpers/lancamentosColumns';

import { useFinanceiroListModule } from './modules/useFinanceiroListModule';
import { useRelatoriosFinanceiroModule } from './modules/useRelatoriosFinanceiroModule';

import DialogBaixaAgrupada from './BaixaAgrupada/DialogBaixaAgrupada';
import { useBaixaAgrupadaModule } from './BaixaAgrupada/modules/useBaixaAgrupadaModule';

import DialogBaixas from './Baixas/DialogBaixas';
import { useBaixasModule } from './Baixas/modules/useBaixasModule';

import DialogComprovantes from './Comprovantes/DialogComprovantes';
import { useComprovantesModule } from './Comprovantes/modules/useComprovantesModule';

// Consulta independente do formulário de inclusão/exclusão de comprovantes.
function ComprovantesDetailPanel({ uuid, isMobile }) {
    const [itens, setItens] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [tentativa, setTentativa] = useState(0);
    const [preview, setPreview] = useState(null);
    const [baixando, setBaixando] = useState(false);
    const [erroDownload, setErroDownload] = useState('');
    const downloadRef = useRef(null);

    useEffect(
        () => () => {
            downloadRef.current?.abort();
        },
        []
    );

    useEffect(() => {
        let cancelado = false;
        setItens([]);
        setErro('');
        setIsLoading(true);
        const carregar = async () => {
            try {
                const response = await LancamentosAPI.listarComprovantes(0, 200, '[]', '[]', '', '[]', uuid);
                if (cancelado) return;
                const comprovantes = response?.data?.data?.[0]?.lancamentoComprovantes;
                setItens(Array.isArray(comprovantes) ? comprovantes : []);
            } catch (error) {
                if (!cancelado) setErro(error?.response?.data?.message || 'Não foi possível carregar os documentos anexos.');
            } finally {
                if (!cancelado) setIsLoading(false);
            }
        };
        if (uuid) carregar();
        else {
            setErro('Lançamento sem identificador para consultar os documentos.');
            setIsLoading(false);
        }
        return () => {
            cancelado = true;
        };
    }, [uuid, tentativa]);

    const arquivoInfo = (item) => {
        const nome = typeof item?.arquivo === 'string' ? item.arquivo : '';
        const isPdf = /\.pdf$/i.test(nome);
        return {
            nome: item?.nomeOriginal || nome,
            url: nome ? getApiUrl(`${isPdf ? 'pdf' : 'imagens'}/${nome}`) : '',
            isPdf,
            isImagem: /\.(png|jpe?g|gif|webp|bmp|avif)$/i.test(nome),
        };
    };

    const baixar = async (item) => {
        const arquivo = arquivoInfo(item);
        if (!arquivo.url || downloadRef.current) return;
        const controller = new AbortController();
        downloadRef.current = controller;
        setBaixando(true);
        setErroDownload('');
        try {
            const response = await fetch(arquivo.url, { signal: controller.signal });
            if (!response.ok) throw new Error(`Não foi possível baixar o arquivo (HTTP ${response.status}).`);
            const blob = await response.blob();
            if (controller.signal.aborted) return;
            if (blob.type.includes('text/html')) throw new Error('O servidor retornou uma página em vez do arquivo.');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            try {
                link.href = url;
                link.download = arquivo.nome || 'documento';
                document.body.appendChild(link);
                link.click();
            } finally {
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
        } catch (error) {
            if (!controller.signal.aborted) setErroDownload(error.message || 'Não foi possível baixar o arquivo.');
        } finally {
            if (downloadRef.current === controller) downloadRef.current = null;
            if (!controller.signal.aborted) setBaixando(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'arquivo',
                header: 'Arquivo',
                size: 420,
                Cell: ({ row }) => row.original.nomeOriginal || row.original.arquivo || 'Documento sem arquivo',
                muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
            },
            {
                accessorKey: 'ativo',
                header: 'Ativo',
                size: 80,
                Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
            },
        ],
        []
    );

    return (
        <Box sx={{ p: { xs: 0, sm: 1 }, minWidth: 0, width: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Documentos anexos — somente consulta
            </Typography>
            {erro && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {erro}
                </Alert>
            )}
            {erroDownload && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {erroDownload}
                </Alert>
            )}
            <MaterialReactTable
                columns={columns}
                data={itens}
                localization={MRT_Localization_PT_BR}
                getRowId={(row, index) => String(row.uuid || row.id || index)}
                enableRowActions
                enableRowSelection={false}
                enableEditing={false}
                enableGlobalFilterModes={false}
                manualFiltering={false}
                manualSorting={false}
                manualPagination={false}
                positionActionsColumn="first"
                initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 5 } }}
                state={{ isLoading }}
                muiSearchTextFieldProps={{ placeholder: 'Pesquisar arquivo' }}
                renderTopToolbarCustomActions={() => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Button type="button" disabled={isLoading || baixando} onClick={() => setTentativa((v) => v + 1)}>
                            Atualizar lista
                        </Button>
                        <Typography variant="body2">Total: {itens.length}</Typography>
                    </Box>
                )}
                renderRowActions={
                    !isMobile
                        ? ({ row }) => (
                              <Box sx={{ display: 'flex' }}>
                                  <IconButton
                                      type="button"
                                      title="Visualizar"
                                      aria-label="Visualizar"
                                      disabled={!row.original.arquivo}
                                      onClick={() => setPreview(arquivoInfo(row.original))}
                                  >
                                      <Visibility />
                                  </IconButton>
                                  <IconButton
                                      type="button"
                                      title="Baixar"
                                      aria-label="Baixar"
                                      disabled={baixando || !row.original.arquivo}
                                      onClick={() => baixar(row.original)}
                                  >
                                      <Download />
                                  </IconButton>
                              </Box>
                          )
                        : undefined
                }
                renderRowActionMenuItems={
                    isMobile
                        ? ({ row, closeMenu }) => [
                              <MenuItem
                                  key="visualizar"
                                  disabled={!row.original.arquivo}
                                  onClick={() => {
                                      closeMenu();
                                      setPreview(arquivoInfo(row.original));
                                  }}
                              >
                                  Visualizar
                              </MenuItem>,
                              <MenuItem
                                  key="baixar"
                                  disabled={baixando || !row.original.arquivo}
                                  onClick={() => {
                                      closeMenu();
                                      baixar(row.original);
                                  }}
                              >
                                  Baixar
                              </MenuItem>,
                          ]
                        : undefined
                }
            />
            <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="lg" fullScreen={isMobile}>
                <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{preview?.nome || 'Documento'}</DialogTitle>
                <DialogContent sx={{ height: '75vh', p: 1 }}>
                    {preview?.isPdf ? (
                        <Box
                            component="iframe"
                            title="Visualização do documento"
                            src={preview.url}
                            sx={{ border: 0, width: '100%', height: '100%' }}
                        />
                    ) : preview?.isImagem ? (
                        <Box
                            component="img"
                            src={preview.url}
                            alt={preview.nome}
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <Alert severity="info">Visualização indisponível para este formato. Utilize Baixar.</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    {preview?.url && (
                        <Button component="a" href={preview.url} target="_blank" rel="noopener noreferrer">
                            Abrir em outra aba
                        </Button>
                    )}
                    <Button type="button" onClick={() => setPreview(null)}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

const Index = () => {
    const hoje = new Date();
    const { primeiroDia, ultimoDia } = obterInicioEFimDoMes(hoje);

    const [lancamentos, setLancamentos] = useState([]);
    const [optionsCentroCusto, setCC] = useState([]);
    const [optionsSituacao, setSituacao] = useState([]);

    const [token] = useState(localStorage.getItem('token') || '');
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const location = useLocation();
    const newUrlParams = new URLSearchParams(location.search);
    const [urlParams, setUrlParams] = useState(new URLSearchParams(location.search));

    const routeParams = getFinanceRouteParams(location.search);
    const tipo = routeParams.tipo;
    const ul = routeParams.ul;
    const li = routeParams.li;
    const operacaoUUID = routeParams.operacaoUUID;
    const uuid = routeParams.uuid;
    const btnText = routeParams.btnText;
    const origem = routeParams.origem;
    const [hasChanged, setHasChanged] = useState(routeParams.hasChanged);

    const pathname = '/Lancamentos/CreateOrEdit';

    const params = buildFinanceParams({
        ul,
        li,
        tipo,
        operacaoUUID,
        uuid,
        btnText,
        origem,
    });

    const isFolhaDePessoal = origem === 'FOLHA_DE_PESSOAL';

    const { parametro } = useContext(Context);
    const { pessoaId, empresaId, empresaIds } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);

    const dispatch = useDispatch();
    const selectedUUIDs = useSelector((state) => state.selectedUUIDs);

    const mrt = useMRTState({
        storageKey: `financeiro.${tipo}.${origem || 'normal'}`,
        pageIndexDefault: 0,
        pageSizeDefault: parametro?.paginacao || 10,
    });

    const [mrtBooted, setMrtBooted] = useState(false);

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});

    const [openBaixaAgrupada, setOpenBaixaAgrupada] = useState(false);

    const [openBaixas, setOpenBaixas] = useState(false);
    const [selectedLancamentoBaixas, setSelectedLancamentoBaixas] = useState(null);

    const [openComprovantes, setOpenComprovantes] = useState(false);
    const [uuidComprovantes, setUuidComprovantes] = useState(null);

    localStorage.setItem('path', JSON.stringify(window.location.pathname));

    useEffect(() => {
        if (mrtBooted) return;

        const dataFilter = Array.isArray(mrt.columnFilters) ? mrt.columnFilters.find((f) => f.id === 'dataVencimento') : null;

        const hasDateRange =
            dataFilter &&
            Array.isArray(dataFilter.value) &&
            dataFilter.value.length === 2 &&
            dataFilter.value[0] &&
            dataFilter.value[1];

        const hasDateFilterFn = mrt.columnFilterFns?.dataVencimento === 'between';
        const hasSorting = Array.isArray(mrt.sorting) && mrt.sorting.length > 0;

        let changed = false;

        if (!hasDateFilterFn) {
            mrt.setColumnFiltersMode({ dataVencimento: 'between' });
            changed = true;
        }

        const filtrosBoot = [
            { id: 'dataVencimento', value: [primeiroDia, ultimoDia] },
            { id: 'provisao', value: '0' },
            { id: 'custoFilial', value: '0' },
        ];

        if (isFolhaDePessoal) {
            filtrosBoot.push({
                id: 'centroCusto.folhaPagamento',
                value: '1',
            });
        }
        if (!hasDateRange) {
            mrt.setColumnFilters(filtrosBoot);
            changed = true;
        }

        if (!hasSorting) {
            mrt.setSorting([{ id: 'dataVencimento', desc: true }]);
            changed = true;
        }

        if (changed) return;

        setMrtBooted(true);
    }, [mrtBooted, primeiroDia, ultimoDia, mrt.columnFilters, mrt.columnFilterFns, mrt.sorting]);

    useEffect(() => {
        if (!isFolhaDePessoal) return;

        const hasFiltroFolha = mrt.columnFilters?.some((f) => f.id === 'centroCusto.folhaPagamento' && f.value === '1');

        if (hasFiltroFolha) return;

        mrt.setColumnFilters((prev = []) => [
            ...prev.filter((f) => f.id !== 'centroCusto.folhaPagamento'),
            {
                id: 'centroCusto.folhaPagamento',
                value: '1',
            },
        ]);
    }, [isFolhaDePessoal, mrt.columnFilters]);

    // corrigir o formato da data no mrt
    useEffect(() => {
        if (!precisaCorrigirDataColumnFiltersObjeto(mrt.columnFilters)) return;
        mrt.setColumnFilters(corrigirDataColumnFiltersObjeto(mrt.columnFilters));
    }, [mrt.columnFilters]);

    const { fetchData } = useFinanceiroListModule({
        mrt,
        tipo,
        setLancamentos,
    });

    const { fluxoCaixa, imprimirLancamentos, imprimirRecibos, vendasDiaria, vendasPeriodo, conciliacaoContas } =
        useRelatoriosFinanceiroModule({
            mrt,
            urlParams,
            lancamentos,
            empresaIds,
        });

    const baixaAgrupadaModule = useBaixaAgrupadaModule({
        token,
        tipo,
        empresaId,
        selectedUUIDs,
        setFlashMessage,
        handleSubmit: async ({ itens, dados }) => {
            try {
                const payload = {
                    ...itens,
                    ...dados,
                };

                const response = await LancamentosAPI.baixas(payload);

                setFlashMessage(response.data.message, response.data.type);

                if (response.data.type === 'success') {
                    setOpenBaixaAgrupada(false);
                    dispatch(clearSelectedComponent());
                    fetchData();
                }
            } catch (error) {
                console.error(error);
                setFlashMessage(
                    error?.response?.data?.message || 'Erro ao realizar baixa agrupada.',
                    error?.response?.data?.type || 'error'
                );
            }
        },
    });

    const baixasModule = useBaixasModule({
        uuid: selectedLancamentoBaixas?.uuid || selectedLancamentoBaixas?.id || '',
        tipo,
        open: openBaixas,
        setFlashMessage,
        onSuccess: () => {
            fetchData();
        },
    });

    const comprovantesModule = useComprovantesModule({
        uuid: uuidComprovantes,
        open: openComprovantes,
        setFlashMessage,
    });

    const consultaSituacao = async ({ empresaId, campo, campos, operacaoSigla }) => {
        const { data } = await SituacaoAPI.situacao({ empresaId, campo, campos, operacaoSigla });

        setSituacao(
            data.map((item) => ({
                text: item.descricao,
                value: item.descricao,
            }))
        );
    };

    const consultaCC = async () => {
        const result = await EmpresaAPI.centroCustos(empresaId, null, tipo, null, null, null);

        setCC(
            result.map((item) => ({
                text: item.descricao,
                value: item.descricao,
            }))
        );
    };

    const handleOpenDialog = (row) => {
        setOpenDialog(true);
        setSelectedRow(row);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmDialog = async () => {
        let msgType = 'success';

        if (selectedRow) {
            try {
                const response = await LancamentosAPI.deletar(selectedRow);
                setFlashMessage(response.data.message, msgType);
                fetchData();
            } catch (error) {
                msgType = 'error';
                console.error(error);
                setFlashMessage(error.response.data.message, msgType);
            }
        }

        setOpenDialog(false);
    };

    const handleNovo = () => {
        params.delete('uuid');
        params.set('btnText', 'Cadastrar');
        navigate(pathname, params);
    };

    const handleBaixaLancamento = (table) => {
        dispatch(clearSelectedComponent());

        const uuids = table.getSelectedRowModel().flatRows.map((row) => row.id);
        if (!uuids.length) return;

        dispatch(updateSelectedComponents(uuids));
        setOpenBaixaAgrupada(true);
    };

    const handleCancelarBaixas = (row) => {
        setSelectedLancamentoBaixas(row.original || row);
        setOpenBaixas(true);
    };

    const handleComprovantes = (row) => {
        setUuidComprovantes(row.id);
        setOpenComprovantes(true);
    };

    useEffect(() => {
        consultaCC();
    }, [tipo]);

    useEffect(() => {
        if (!mrtBooted) return;

        fetchData();
    }, [mrtBooted, tipo, mrt.columnFilters, mrt.globalFilter, mrt.pagination.pageIndex, mrt.pagination.pageSize, mrt.sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) return;

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    useEffect(() => {
        if (!token) {
            return navigate('/Auth', { replace: true });
        }

        const areUrlParamsDifferent = () => {
            const currentKeys = Array.from(urlParams.keys());
            const newKeys = Array.from(newUrlParams.keys());

            if (currentKeys.length !== newKeys.length) return true;

            for (let i = 0; i < currentKeys.length; i++) {
                if (currentKeys[i] !== newKeys[i]) return true;
            }

            for (const key of currentKeys) {
                if (urlParams.get(key) !== newUrlParams.get(key)) return true;
            }

            return false;
        };

        if (areUrlParamsDifferent()) {
            setUrlParams(newUrlParams);
        }
    }, [token, location.search, urlParams, ul, li, navigate, pessoaId, empresaId, newUrlParams]);

    useEffect(() => {
        consultaSituacao({ empresaId: empresaTabelaId, campo: 'lancamento' });
    }, [empresaTabelaId]);

    let titulo = tipo === 'Pagar' ? 'Lançamentos a Pagar' : 'Lançamentos a Receber';

    if (Array.isArray(mrt.columnFilters) && mrt.columnFilters.length) {
        for (const filters of mrt.columnFilters) {
            const { id: columnId, value: filterValue } = filters;

            if (columnId !== 'dataVencimento') continue;

            if (mrt.columnFilterFns[columnId] === 'equals') {
                titulo += ` em ${format(filterValue, 'dd/MM/yyyy')}`;
            } else if (mrt.columnFilterFns[columnId] === 'greaterThanOrEqualTo') {
                titulo += ` maior ou igual a ${format(filterValue, 'dd/MM/yyyy')}`;
            } else if (mrt.columnFilterFns[columnId] === 'lessThanOrEqualTo') {
                titulo += ` menor ou igual a ${format(filterValue, 'dd/MM/yyyy')}`;
            } else if (mrt.columnFilterFns[columnId] === 'between') {
                const [start, end] = filterValue || [];
                if (start || end) {
                    titulo += ` no período de ${isValid(start) ? format(start, 'dd/MM/yyyy') : 'início inválido'} a ${
                        isValid(end) ? format(end, 'dd/MM/yyyy') : 'fim inválido'
                    }`;
                }
            } else if (mrt.columnFilterFns[columnId] === 'betweenInclusive') {
                const [start, end] = filterValue || [];
                if (start || end) {
                    titulo += ` no período diferente de ${isValid(start) ? format(start, 'dd/MM/yyyy') : 'início inválido'} a ${
                        isValid(end) ? format(end, 'dd/MM/yyyy') : 'fim inválido'
                    }`;
                }
            }
        }
    }

    const usaSubCentroCusto = parametro.usaSubCentroCusto;
    const columns = useMemo(
        () =>
            getLancamentosColumns({
                titulo,
                optionsCentroCusto,
                optionsSituacao,
                tipo,
                isFolhaDePessoal,
                usaSubCentroCusto,
            }),
        [titulo, optionsCentroCusto, optionsSituacao, tipo, isFolhaDePessoal, usaSubCentroCusto]
    );
    //funções do MRT
    const empresaAtualId = Number(empresaId);
    const empresasPermitidas = JSON.parse(localStorage.getItem('empresaIds') || '[]').map(Number);

    const usuarioPodeAcessarEmpresa = (registro) =>
        empresaAtualId === Number(registro?.empresaId) || empresasPermitidas.includes(Number(registro?.empresaId));

    const handleEditarLancamento = (row) => {
        params.set('uuid', row.id);
        params.set('btnText', 'Editar');
        navigate(pathname, params);
    };

    const handleBaixarLancamento = (row) => {
        params.set('uuid', row.id);
        params.set('btnText', 'Baixar');
        navigate(pathname, params);
    };

    const handleRecibo = async (registro) => {
        const response = await relatorios('reciboLancamentoAberto', 'reciboLancamentoAberto', registro);

        const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
        });

        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
    };

    const theme = tableThemeCustom();

    const themeMRT = useTheme();
    const isMobile = useMediaQuery(themeMRT.breakpoints.down('sm'));

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        isMobile,
    });

    return (
        <div>
            {openDialog && (
                <CustomDialog
                    title={`Delete lançamento a ${tipo}!`}
                    content="Você tem certeza?"
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirmDialog}
                />
            )}

            <DialogBaixaAgrupada
                open={openBaixaAgrupada}
                onClose={() => {
                    setOpenBaixaAgrupada(false);
                    dispatch(clearSelectedComponent());
                }}
                {...baixaAgrupadaModule}
            />

            <DialogBaixas
                open={openBaixas}
                onClose={() => {
                    setOpenBaixas(false);
                    setSelectedLancamentoBaixas(null);
                }}
                {...baixasModule}
            />

            {openComprovantes && (
                <DialogComprovantes
                    open={openComprovantes}
                    onClose={() => {
                        setOpenComprovantes(false);
                        setUuidComprovantes(null);
                    }}
                    {...comprovantesModule}
                />
            )}

            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={lancamentos}
                    getRowId={(row) => row.uuid}
                    enableExpanding
                    enableExpandAll={false}
                    positionExpandColumn="first"
                    getRowCanExpand={(row) => !!row.original?.uuid && usuarioPodeAcessarEmpresa(row.original)}
                    renderDetailPanel={({ row }) =>
                        row.getIsExpanded() && usuarioPodeAcessarEmpresa(row.original) ? (
                            <ComprovantesDetailPanel key={row.original.uuid} uuid={row.original.uuid} isMobile={isMobile} />
                        ) : null
                    }
                    muiExpandButtonProps={({ row }) => ({
                        title: row.getIsExpanded() ? 'Recolher documentos' : 'Consultar documentos anexos',
                        'aria-label': row.getIsExpanded() ? 'Recolher documentos' : 'Consultar documentos anexos',
                    })}
                    renderRowActions={
                        !isMobile
                            ? ({ row }) => {
                                  const registro = row.original;

                                  const podeEditarRegistro =
                                      podeEditar() && usuarioPodeAcessarEmpresa(registro) && registro?.situacaoId === 1;

                                  const podeBaixarRegistro =
                                      podeBaixar() && usuarioPodeAcessarEmpresa(registro) && Number(registro?.valorAberto) > 0;

                                  const podeEstornarRegistro =
                                      podeCancelarBaixa() &&
                                      usuarioPodeAcessarEmpresa(registro) &&
                                      Number(registro?.valorBaixado) > 0;

                                  const podeDeletarRegistro = podeCancelar() && Number(registro?.valorBaixado) === 0;

                                  return (
                                      <Box
                                          sx={{
                                              display: 'flex',
                                              flexWrap: 'nowrap',
                                              gap: '1px',
                                          }}
                                      >
                                          <Tooltip title="Editar">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          handleEditarLancamento(row);
                                                      }}
                                                      disabled={!podeEditarRegistro}
                                                  >
                                                      <Edit fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip title="Baixar lançamento">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          handleBaixarLancamento(row);
                                                      }}
                                                      disabled={!podeBaixarRegistro}
                                                  >
                                                      <MonetizationOn fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip title="Estornar baixa">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          handleCancelarBaixas(row);
                                                      }}
                                                      disabled={!podeEstornarRegistro}
                                                  >
                                                      <MoneyOff fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip title="Anexar documento">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          handleComprovantes(row);
                                                      }}
                                                      disabled={!podeEditar()}
                                                  >
                                                      <PictureAsPdf fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          {parametro.recibo80Col && (
                                              <Tooltip title="Emitir recibo 80 colunas">
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          color="info"
                                                          onClick={(e) => {
                                                              e.preventDefault();
                                                              e.stopPropagation();
                                                              handleRecibo(registro);
                                                          }}
                                                          disabled={!podeImprimir()}
                                                      >
                                                          <PrintIcon fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                          )}

                                          <Tooltip title="Deletar">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          handleOpenDialog(row);
                                                      }}
                                                      disabled={!podeDeletarRegistro}
                                                  >
                                                      <Delete fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                      </Box>
                                  );
                              }
                            : undefined
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => {
                                  const registro = row.original;

                                  const podeEditarRegistro =
                                      podeEditar() && usuarioPodeAcessarEmpresa(registro) && registro?.situacaoId === 1;

                                  const podeBaixarRegistro =
                                      podeBaixar() && usuarioPodeAcessarEmpresa(registro) && Number(registro?.valorAberto) > 0;

                                  const podeEstornarRegistro =
                                      podeCancelarBaixa() &&
                                      usuarioPodeAcessarEmpresa(registro) &&
                                      Number(registro?.valorBaixado) > 0;

                                  const podeDeletarRegistro = podeCancelar() && Number(registro?.valorBaixado) === 0;

                                  return [
                                      <MenuItem
                                          key="editar"
                                          disabled={!podeEditarRegistro}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleEditarLancamento(row);
                                              closeMenu();
                                          }}
                                      >
                                          <ListItemIcon>
                                              <Edit fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>Editar</ListItemText>
                                      </MenuItem>,

                                      <MenuItem
                                          key="baixar"
                                          disabled={!podeBaixarRegistro}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleBaixarLancamento(row);
                                              closeMenu();
                                          }}
                                      >
                                          <ListItemIcon>
                                              <MonetizationOn color="error" fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>Baixar lançamento</ListItemText>
                                      </MenuItem>,

                                      <MenuItem
                                          key="estornar"
                                          disabled={!podeEstornarRegistro}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleCancelarBaixas(row);
                                              closeMenu();
                                          }}
                                      >
                                          <ListItemIcon>
                                              <MoneyOff color="error" fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>Estornar baixa</ListItemText>
                                      </MenuItem>,

                                      <MenuItem
                                          key="comprovantes"
                                          disabled={!podeEditar()}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleComprovantes(row);
                                              closeMenu();
                                          }}
                                      >
                                          <ListItemIcon>
                                              <PictureAsPdf color="error" fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>Anexar documento</ListItemText>
                                      </MenuItem>,

                                      parametro.recibo80Col && (
                                          <MenuItem
                                              key="recibo"
                                              disabled={!podeImprimir()}
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleRecibo(registro);
                                                  closeMenu();
                                              }}
                                          >
                                              <ListItemIcon>
                                                  <PrintIcon color="info" fontSize="small" />
                                              </ListItemIcon>
                                              <ListItemText>Emitir recibo 80 colunas</ListItemText>
                                          </MenuItem>
                                      ),

                                      <MenuItem
                                          key="deletar"
                                          disabled={!podeDeletarRegistro}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleOpenDialog(row);
                                              closeMenu();
                                          }}
                                      >
                                          <ListItemIcon>
                                              <Delete color="error" fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>Deletar</ListItemText>
                                      </MenuItem>,
                                  ].filter(Boolean);
                              }
                            : undefined
                    }
                    renderTopToolbarCustomActions={({ table }) => {
                        const tipoAtual = urlParams.get('tipo') ?? '';

                        const actions = [
                            {
                                key: 'novo',
                                label: 'Novo',
                                tooltip: 'Novo Registro',
                                color: 'secondary',
                                onClick: handleNovo,
                                disabled: !podeIncluir(),
                            },
                            {
                                key: 'baixar',
                                label: 'Baixar',
                                tooltip: `Baixar lançamentos a ${tipoAtual} selecionados`,
                                onClick:
                                    !table.getIsSomeRowsSelected() && !table.getIsAllPageRowsSelected()
                                        ? undefined
                                        : () => handleBaixaLancamento(table),
                                disabled: !podeBaixar() || (!table.getIsSomeRowsSelected() && !table.getIsAllPageRowsSelected()),
                                color: 'success',
                            },
                            {
                                key: 'imprimir',
                                label: 'Imprimir',
                                tooltip: `Imprimir lançamentos a ${tipoAtual}`,
                                onClick: () => imprimirLancamentos({ table, baixados: false }),
                                disabled: !podeImprimir(),
                                show: podeImprimir(),
                                color: 'info',
                            },
                            {
                                key: 'recibo',
                                label: 'Recibos',
                                tooltip: `Emitir recibos a ${tipoAtual} selecionados`,
                                onClick:
                                    !table.getIsSomeRowsSelected() && !table.getIsAllPageRowsSelected()
                                        ? undefined
                                        : () => imprimirRecibos(table, lancamentos),
                                disabled:
                                    !podeImprimir() || (!table.getIsSomeRowsSelected() && !table.getIsAllPageRowsSelected()),
                                color: 'info',
                            },
                            ...(lancamentos?.length
                                ? [
                                      {
                                          key: 'fluxo-caixa',
                                          label: 'Fluxo Caixa',
                                          tooltip: `Imprimir Fluxo de Caixa no período`,
                                          onClick: () => fluxoCaixa({ table, baixados: true }),
                                          disabled: !podeImprimir(),
                                          show: podeImprimirGerencia(),
                                          color: 'primary',
                                      },
                                      {
                                          key: 'imprimir-baixados',
                                          label: 'Baixados',
                                          tooltip: `Imprimir lançamentos à ${tipoAtual} baixados no período, mostra por quais contas os lançamentos foram baixados`,
                                          onClick: () => imprimirLancamentos({ table, baixados: true }),
                                          disabled: !podeImprimir(),
                                          show: podeImprimirGerencia(),
                                          color: 'primary',
                                      },
                                      {
                                          key: 'vendasDiaria',
                                          label: 'Vendas (D)',
                                          tooltip:
                                              'Relatório de vendas diárias pelo sistema financeiro, totaliza por lançamentos baixados',
                                          onClick: () => vendasDiaria(),
                                          disabled: !podeImprimir(),
                                          show: podeImprimirAnalitico() && tipo === 'Receber',
                                          color: 'secondary',
                                      },
                                      {
                                          key: 'vendasPeriodo',
                                          label: 'Vendas (A)',
                                          tooltip: 'Relatório de vendas agrupados por forma de recebimento, lançamentos baixados',
                                          onClick: () => vendasPeriodo(),
                                          disabled: !podeImprimir(),
                                          show: podeImprimirAnalitico() && tipo === 'Receber',
                                          color: 'secondary',
                                      },
                                      {
                                          key: 'conciliacaoContas',
                                          label: 'Conciliação de Contas',
                                          tooltip: 'Relatório de conciliação de contas, títulos baixados por empresa e por conta',
                                          onClick: () => conciliacaoContas(),
                                          disabled: !podeImprimir(),
                                          show: podeImprimirGerencia() && tipo === 'Pagar',
                                          color: 'primary',
                                      },
                                  ]
                                : []),
                        ].filter((a) => a?.show ?? true);

                        return <ResponsiveToolbarActions actions={actions} />;
                    }}
                />
            </ThemeProvider>
        </div>
    );
};

export default Index;
